import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Search, Pencil, Trash2, Star, Phone, Mail, MapPin, Zap, Share2, UserPlus, Check } from 'lucide-react';
import { providerService } from '../services/providerService';
import featureFlagService from '../services/featureFlagService';

const CATEGORIES = ['plumbing', 'electrical', 'hvac', 'appliance', 'structural', 'pest', 'security', 'general'];

const CATEGORY_LABELS = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'HVAC',
  appliance: 'Appliance',
  structural: 'Structural',
  pest: 'Pest Control',
  security: 'Security',
  general: 'General'
};

const CATEGORY_COLORS = {
  plumbing: 'bg-blue-100 text-blue-800',
  electrical: 'bg-yellow-100 text-yellow-800',
  hvac: 'bg-cyan-100 text-cyan-800',
  appliance: 'bg-purple-100 text-purple-800',
  structural: 'bg-orange-100 text-orange-800',
  pest: 'bg-red-100 text-red-800',
  security: 'bg-gray-100 text-gray-800',
  general: 'bg-green-100 text-green-800'
};

const emptyForm = {
  name: '',
  company: '',
  categories: [],
  contact: { phone: '', email: '' },
  location: { address: '', city: '', state: '', zipCode: '' },
  availability: 'Mon-Fri 8am-6pm',
  hourlyRate: '',
  emergencyAvailable: false,
  status: 'active'
};

const StarRating = ({ rating }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-3.5 w-3.5 ${star <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">
        {rating > 0 ? `${rating.toFixed(1)}` : 'No rating'}
      </span>
    </div>
  );
};

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [vendorSharingEnabled, setVendorSharingEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState('mine');
  const [sharingIds, setSharingIds] = useState({}); // providerId -> true while toggle in flight
  const [sharedProviders, setSharedProviders] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [sharedCategoryFilter, setSharedCategoryFilter] = useState('');
  const [sharedRadius, setSharedRadius] = useState(25);
  const [sharedMessage, setSharedMessage] = useState('');
  const [addedIds, setAddedIds] = useState({}); // providerId -> true once cloned this session

  useEffect(() => {
    featureFlagService.isFeatureEnabled('VENDOR_SHARING_ENABLED').then(setVendorSharingEnabled).catch(() => {});
  }, []);

  useEffect(() => {
    loadProviders();
  }, [categoryFilter]);

  useEffect(() => {
    if (vendorSharingEnabled && activeTab === 'shared') {
      loadSharedProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorSharingEnabled, activeTab, sharedCategoryFilter, sharedRadius]);

  const loadSharedProviders = async () => {
    try {
      setSharedLoading(true);
      setSharedMessage('');
      const data = await providerService.getShared({
        category: sharedCategoryFilter || undefined,
        radius: sharedRadius
      });
      setSharedProviders(data.providers || []);
      if (data.message) setSharedMessage(data.message);
    } catch (err) {
      console.error('Failed to load shared providers:', err);
      setSharedMessage('Failed to load nearby shared vendors.');
    } finally {
      setSharedLoading(false);
    }
  };

  const handleShareToggle = async (provider) => {
    setSharingIds(prev => ({ ...prev, [provider._id]: true }));
    try {
      const updated = await providerService.shareToggle(provider._id, !provider.isShared);
      setProviders(prev => prev.map(p => (p._id === provider._id ? { ...p, isShared: updated.isShared } : p)));
    } catch (err) {
      console.error('Failed to update sharing status:', err);
    } finally {
      setSharingIds(prev => {
        const next = { ...prev };
        delete next[provider._id];
        return next;
      });
    }
  };

  const handleAddSharedProvider = async (provider) => {
    try {
      await providerService.cloneProvider(provider._id);
      setAddedIds(prev => ({ ...prev, [provider._id]: true }));
    } catch (err) {
      console.error('Failed to add shared provider:', err);
    }
  };

  const loadProviders = async () => {
    try {
      setLoading(true);
      const data = await providerService.getAll({ category: categoryFilter || undefined });
      setProviders(data);
    } catch (err) {
      console.error('Failed to load providers:', err);
      setError('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = () => {
    setFormData(emptyForm);
    setError('');
    setIsAddModalOpen(true);
  };

  const handleEditProvider = (provider) => {
    setSelectedProvider(provider);
    setFormData({
      name: provider.name || '',
      company: provider.company || '',
      categories: provider.categories || [],
      contact: {
        phone: provider.contact?.phone || '',
        email: provider.contact?.email || ''
      },
      location: {
        address: provider.location?.address || '',
        city: provider.location?.city || '',
        state: provider.location?.state || '',
        zipCode: provider.location?.zipCode || ''
      },
      availability: provider.availability || 'Mon-Fri 8am-6pm',
      hourlyRate: provider.hourlyRate != null ? String(provider.hourlyRate) : '',
      emergencyAvailable: provider.emergencyAvailable || false,
      status: provider.status || 'active'
    });
    setError('');
    setIsEditModalOpen(true);
  };

  const handleDeleteProvider = (provider) => {
    setSelectedProvider(provider);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (formData.categories.length === 0) {
      setError('Please select at least one category.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = buildPayload(formData);
      await providerService.create(payload);
      setIsAddModalOpen(false);
      loadProviders();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add provider');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (formData.categories.length === 0) {
      setError('Please select at least one category.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = buildPayload(formData);
      await providerService.update(selectedProvider._id, payload);
      setIsEditModalOpen(false);
      loadProviders();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update provider');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    try {
      await providerService.delete(selectedProvider._id);
      setIsDeleteModalOpen(false);
      loadProviders();
    } catch (err) {
      console.error('Failed to delete provider:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const buildPayload = (data) => ({
    name: data.name,
    company: data.company,
    categories: data.categories,
    contact: data.contact,
    location: data.location,
    availability: data.availability,
    hourlyRate: data.hourlyRate !== '' ? Number(data.hourlyRate) : undefined,
    emergencyAvailable: data.emergencyAvailable,
    status: data.status
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNestedChange = (group, field, value) => {
    setFormData(prev => ({
      ...prev,
      [group]: { ...prev[group], [field]: value }
    }));
  };

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  const filteredProviders = providers.filter(p => {
    const q = searchQuery.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.company?.toLowerCase().includes(q) ||
      p.contact?.email?.toLowerCase().includes(q) ||
      p.location?.city?.toLowerCase().includes(q)
    );
  });

  const renderFormFields = () => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
      {error && (
        <div className="bg-red-50 text-red-800 px-4 py-3 rounded text-sm">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required disabled={submitting} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company">Company</Label>
          <Input id="company" name="company" value={formData.company} onChange={handleInputChange} disabled={submitting} placeholder="Company name" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Categories *</Label>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              type="button"
              onClick={() => toggleCategory(cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                formData.categories.includes(cat)
                  ? 'bg-cyan-600 text-white border-cyan-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-cyan-400'
              }`}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contact-phone">Phone</Label>
          <Input
            id="contact-phone"
            value={formData.contact.phone}
            onChange={e => handleNestedChange('contact', 'phone', e.target.value)}
            disabled={submitting}
            placeholder="(555) 123-4567"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-email">Email</Label>
          <Input
            id="contact-email"
            type="email"
            value={formData.contact.email}
            onChange={e => handleNestedChange('contact', 'email', e.target.value)}
            disabled={submitting}
            placeholder="provider@example.com"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="loc-address">Address</Label>
        <Input
          id="loc-address"
          value={formData.location.address}
          onChange={e => handleNestedChange('location', 'address', e.target.value)}
          disabled={submitting}
          placeholder="Street address"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="loc-city">City</Label>
          <Input
            id="loc-city"
            value={formData.location.city}
            onChange={e => handleNestedChange('location', 'city', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="loc-state">State</Label>
          <Input
            id="loc-state"
            value={formData.location.state}
            onChange={e => handleNestedChange('location', 'state', e.target.value)}
            disabled={submitting}
            placeholder="CA"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="loc-zip">ZIP</Label>
          <Input
            id="loc-zip"
            value={formData.location.zipCode}
            onChange={e => handleNestedChange('location', 'zipCode', e.target.value)}
            disabled={submitting}
            placeholder="90210"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="availability">Availability</Label>
          <Input
            id="availability"
            name="availability"
            value={formData.availability}
            onChange={handleInputChange}
            disabled={submitting}
            placeholder="Mon-Fri 8am-6pm"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
          <Input
            id="hourlyRate"
            name="hourlyRate"
            type="number"
            min="0"
            value={formData.hourlyRate}
            onChange={handleInputChange}
            disabled={submitting}
            placeholder="75"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            disabled={submitting}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="emergencyAvailable"
              checked={formData.emergencyAvailable}
              onChange={handleInputChange}
              disabled={submitting}
              className="rounded border-gray-300"
            />
            <span className="text-sm font-medium">Emergency Available</span>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold tracking-tight">Service Providers</h2>
        <Button onClick={handleAddProvider}>
          <Plus className="mr-2 h-4 w-4" /> Add Provider
        </Button>
      </div>

      {vendorSharingEnabled && (
        <div className="flex items-center gap-1 border-b">
          <button
            onClick={() => setActiveTab('mine')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'mine' ? 'border-cyan-600 text-cyan-600' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            My Vendors
          </button>
          <button
            onClick={() => setActiveTab('shared')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'shared' ? 'border-cyan-600 text-cyan-600' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Nearby Shared Vendors
          </button>
        </div>
      )}

      {activeTab === 'shared' && vendorSharingEnabled ? (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={sharedCategoryFilter}
              onChange={e => setSharedCategoryFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
              ))}
            </select>
            <select
              value={sharedRadius}
              onChange={e => setSharedRadius(Number(e.target.value))}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value={10}>Within 10 miles</option>
              <option value={25}>Within 25 miles</option>
              <option value={50}>Within 50 miles</option>
            </select>
          </div>

          {sharedLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : sharedProviders.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">
                  {sharedMessage || 'No shared vendors found nearby yet.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-md border">
              <div className="p-4 space-y-3">
                {sharedProviders.map(provider => (
                  <div
                    key={provider._id}
                    className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="font-medium">{provider.name}</p>
                        {provider.company && (
                          <span className="text-sm text-muted-foreground">· {provider.company}</span>
                        )}
                        {provider.emergencyAvailable && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-xs font-semibold">
                            <Zap className="h-3 w-3" /> Emergency
                          </span>
                        )}
                        <span className="inline-flex items-center rounded-full bg-cyan-50 text-cyan-700 px-2 py-0.5 text-xs font-semibold">
                          {provider.distanceMiles} mi away
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {provider.categories?.map(cat => (
                          <span key={cat} className={`rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-700'}`}>
                            {CATEGORY_LABELS[cat] || cat}
                          </span>
                        ))}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <StarRating rating={provider.rating || 0} />
                        {provider.reviewCount > 0 && (
                          <span>{provider.reviewCount} review{provider.reviewCount !== 1 ? 's' : ''}</span>
                        )}
                        {provider.hourlyRate != null && (
                          <span>${provider.hourlyRate}/hr</span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                        {provider.contact?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {provider.contact.phone}
                          </span>
                        )}
                        {provider.contact?.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {provider.contact.email}
                          </span>
                        )}
                        {(provider.location?.city || provider.location?.state) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {[provider.location.city, provider.location.state].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant={addedIds[provider._id] ? 'outline' : 'default'}
                        size="sm"
                        disabled={!!addedIds[provider._id]}
                        onClick={() => handleAddSharedProvider(provider)}
                      >
                        {addedIds[provider._id] ? (
                          <><Check className="mr-1.5 h-4 w-4" /> Added</>
                        ) : (
                          <><UserPlus className="mr-1.5 h-4 w-4" /> Add to My List</>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
      <>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search providers..."
            className="pl-8"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
          ))}
        </select>
      </div>

      {error && !isAddModalOpen && !isEditModalOpen && !isDeleteModalOpen && (
        <div className="bg-red-50 text-red-800 px-4 py-3 rounded text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredProviders.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">
              {searchQuery || categoryFilter
                ? 'No providers match your search.'
                : 'No service providers yet. Click "Add Provider" to get started.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border">
          <div className="p-4 space-y-3">
            {filteredProviders.map(provider => (
              <div
                key={provider._id}
                className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-medium">{provider.name}</p>
                    {provider.company && (
                      <span className="text-sm text-muted-foreground">· {provider.company}</span>
                    )}
                    {provider.emergencyAvailable && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-xs font-semibold">
                        <Zap className="h-3 w-3" /> Emergency
                      </span>
                    )}
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                      provider.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {provider.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {provider.categories?.map(cat => (
                      <span key={cat} className={`rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_COLORS[cat] || 'bg-gray-100 text-gray-700'}`}>
                        {CATEGORY_LABELS[cat] || cat}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <StarRating rating={provider.rating || 0} />
                    {provider.reviewCount > 0 && (
                      <span>{provider.reviewCount} review{provider.reviewCount !== 1 ? 's' : ''}</span>
                    )}
                    {provider.pastJobCount > 0 && (
                      <span>{provider.pastJobCount} job{provider.pastJobCount !== 1 ? 's' : ''}</span>
                    )}
                    {provider.hourlyRate != null && (
                      <span>${provider.hourlyRate}/hr</span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                    {provider.contact?.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {provider.contact.phone}
                      </span>
                    )}
                    {provider.contact?.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {provider.contact.email}
                      </span>
                    )}
                    {(provider.location?.city || provider.location?.state) && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {[provider.location.city, provider.location.state].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {provider.availability && (
                      <span className="text-xs">{provider.availability}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {vendorSharingEnabled && (
                    <Button
                      variant={provider.isShared ? 'default' : 'ghost'}
                      size="sm"
                      disabled={!!sharingIds[provider._id]}
                      onClick={() => handleShareToggle(provider)}
                      title={provider.isShared ? 'Shared with nearby landlords — click to unshare' : 'Share with nearby landlords'}
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleEditProvider(provider)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteProvider(provider)}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}

      {/* Add Provider Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Add Service Provider</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitAdd}>
            {renderFormFields()}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Provider'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Provider Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Edit Provider</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitEdit}>
            {renderFormFields()}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Provider'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Provider</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{selectedProvider?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsDeleteModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleConfirmDelete} disabled={submitting}>
              {submitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Providers;
